import { DollarSign, TrendingUp, Award, Gift } from 'lucide-react';
import { CommissionTrendChart } from './charts/CommissionTrendChart';
import { TopEarnersChart } from './charts/TopEarnersChart';
import type { PayMetrics } from '../types/dashboard.types';

interface PayReportsSectionProps {
  payMetrics: PayMetrics | null;
}

export const PayReportsSection: React.FC<PayReportsSectionProps> = ({ payMetrics }) => {
  if (!payMetrics) {
    return (
      <div className="mt-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Pay Reports</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">Loading pay report data...</p>
        </div>
      </div>
    );
  }

  const commissionCards = [
    {
      title: 'Total Commissions',
      value: `$${payMetrics.totalCommissions.toLocaleString()}`,
      icon: DollarSign,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: 'Total Overrides',
      value: `$${payMetrics.totalOverrides.toLocaleString()}`,
      icon: TrendingUp,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Total Bonuses',
      value: `$${payMetrics.totalBonuses.toLocaleString()}`,
      icon: Gift,
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    {
      title: 'Avg Commission',
      value: `$${payMetrics.averageCommission.toFixed(2)}`,
      icon: Award,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
    },
  ];

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Pay Reports</h2>

      {/* Commission Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {commissionCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{card.value}</p>
                </div>
                <div className={`${card.bgColor} p-3 rounded-lg`}>
                  <Icon className={`w-6 h-6 ${card.iconColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Commission Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CommissionTrendChart data={payMetrics.commissionTrend} />
        <TopEarnersChart data={payMetrics.topEarners} />
      </div>
    </div>
  );
};
